import { useRouter } from 'next/router'
import { memo, useCallback, useEffect, useMemo } from 'react'

import MenuDotsIcon from 'assets/svg/app/menu-dots.svg'

import { BaseCard } from 'components/Card/style'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'

import { PerpsProvider } from '@kwenta/sdk/types'
import Spacer from 'components/Spacer'
import { GridDivCenteredRow } from 'components/layout/grid'
import { DEFAULT_PERPS_PROVIDER } from 'constants/futures'
import ROUTES from 'constants/routes'

import useClickOutside from 'hooks/useClickOutside'
import useWindowSize from 'hooks/useWindowSize'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import { selectShowBanner } from 'state/app/selectors'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectIsolatedPositionsCount } from 'state/futures/isolatedMargin/selectors'
import { useAppSelector } from 'state/hooks'
import styled from 'styled-components'
import media from 'styles/media'
import { Tab } from 'types/dashboard'
import { type AppItem, SubMenuItemType } from '../constants'
import { useNavDropdown } from './NavDropdown'

type MenuDropdownProps = {
	i18nLabel: string
	apps?: AppItem[]
}

const MenuDropdown = memo(({ apps }: MenuDropdownProps) => {
	const { t } = useTranslation()
	const { deviceType } = useWindowSize()
	const isMobile = useMemo(() => deviceType === 'mobile', [deviceType])
	const { pathname, push } = useRouter()
	const { isOpen, setIsOpen } = useNavDropdown()
	const showBanner = useAppSelector(selectShowBanner)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const isolatedPositionsCount = useAppSelector(selectIsolatedPositionsCount, isEqual)
	const isHome = useMemo(() => pathname === ROUTES.Home.Root, [pathname])
	const showOptimismProvder = useMemo(
		() => isolatedPositionsCount[PerpsProvider.SNX_V2_OP] > 0,
		[isolatedPositionsCount]
	)

	const { ref, excludeRef } = useClickOutside(() => setIsOpen(false))

	const toggleDropdown = useCallback(() => {
		setIsOpen(!isOpen)
	}, [isOpen, setIsOpen])

	const addBannerOffset = useMemo(
		() => showBanner && !isHome && isMobile,
		[showBanner, isHome, isMobile]
	)

	useEffect(() => {
		const handleScroll = () => {
			if (isOpen) {
				setIsOpen(false)
			}
		}

		window.addEventListener('scroll', handleScroll)
		window.addEventListener('wheel', handleScroll)
		window.addEventListener('touchmove', handleScroll)

		return () => {
			window.removeEventListener('scroll', handleScroll)
			window.removeEventListener('wheel', handleScroll)
			window.removeEventListener('touchmove', handleScroll)
		}
	}, [isOpen, setIsOpen])

	return (
		apps &&
		apps.length > 0 && (
			<>
				<IconWrapper onClick={toggleDropdown} ref={ref}>
					<MenuDotsIcon />
				</IconWrapper>
				<ContaierSpacer open={isOpen} $isHome={isHome} $addBannerOffset={addBannerOffset} />
				<DropdownContainer open={isOpen} $isHome={isHome} $addBannerOffset={addBannerOffset}>
					<DropdownContent ref={excludeRef}>
						<AppsContainer>
							{apps
								.filter(({ copy }) => !!copy)
								.map(({ icon, label, copy, href, isActive, subMenu }) => {
									return (
										<FlexDivCol key={label}>
											<SelectionItem
												justifyContent="flex-start"
												columnGap="12px"
												onClick={() =>
													isActive
														? push(ROUTES.Markets.Home(DEFAULT_PERPS_PROVIDER))
														: window.open(href, '_self')
												}
											>
												<LabelIconWrapper $isActive={!!isActive} className={label}>
													{icon}
												</LabelIconWrapper>
												<FlexDivCol>
													<HeaderBody weight="semiBold">{t(label)}</HeaderBody>
													<DescriptionBody color="secondary">{t(copy)}</DescriptionBody>
												</FlexDivCol>
											</SelectionItem>
											{!isHome && subMenu && isMobile && (
												<SubMenuContainer rowGap="12px">
													{subMenu
														.filter(({ type }) =>
															showOptimismProvder
																? type !== SubMenuItemType.DEFAULT
																: type !== SubMenuItemType.OPTIMISM && type !== SubMenuItemType.BASE
														)
														.map(({ i18nLabel, link, tabs, type }) => {
															const activeLink =
																link.includes(pathname.split('/')[1]) &&
																(type === SubMenuItemType.BASE
																	? perpsProvider === PerpsProvider.SNX_V3_BASE
																	: type === SubMenuItemType.OPTIMISM
																		? perpsProvider === PerpsProvider.SNX_V2_OP
																		: true)
															return (
																<FlexDivCol key={i18nLabel}>
																	<SubMenuItem
																		color="secondary"
																		size="large"
																		onClick={() => {
																			push(link)
																			setIsOpen(false)
																		}}
																		$activeLink={activeLink}
																	>
																		{t(i18nLabel)}
																	</SubMenuItem>
																	{activeLink && tabs && (
																		<TabsContainer>
																			{tabs.map(({ i18nLabel, link, tabType }) => {
																				const activeTab =
																					(pathname.split('/')[2] || Tab.Overview) === tabType
																				return (
																					<Body
																						key={i18nLabel}
																						color={activeTab ? 'primary' : 'secondary'}
																						onClick={() => {
																							push(link)
																							setIsOpen(false)
																						}}
																					>
																						{t(i18nLabel)}
																					</Body>
																				)
																			})}
																		</TabsContainer>
																	)}
																</FlexDivCol>
															)
														})}
												</SubMenuContainer>
											)}
										</FlexDivCol>
									)
								})}
							<Divider height={1} divider />
							<MenuFooter>
								{apps
									.filter(({ copy }) => !copy)
									.map(({ label, icon, href }) => (
										<FooterItem
											key={label}
											justifyContent="flex-start"
											columnGap="8px"
											onClick={() => window.open(href, '_self')}
										>
											{icon}
											<FooterBody color="secondary" size="large">
												{t(label)}
											</FooterBody>
										</FooterItem>
									))}
							</MenuFooter>
						</AppsContainer>
					</DropdownContent>
				</DropdownContainer>
			</>
		)
	)
})

const TabsContainer = styled(FlexDivCol)`
	gap: 12px;
	margin-top: 12px;
	margin-left: 12px;
	border-left: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding-left: 12px;
`

const DescriptionBody = styled(Body)``

const SubMenuItem = styled(Body)<{ $activeLink: boolean }>`
	cursor: pointer;

	color: ${(props) =>
		props.$activeLink
			? props.theme.colors.selectedTheme.newTheme.text.primary
			: props.theme.colors.selectedTheme.newTheme.text.secondary};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	}
`

const SubMenuContainer = styled(FlexDivCol)`
	margin-left: 58px;
	margin-top: 12px;
	border-left: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding-left: 12px;
	gap: 12px;
`

const HeaderBody = styled(Body)`
	font-size: 16px;
`

const SelectionItem = styled(FlexDivRowCentered)`
	cursor: pointer;
	transition: 0.3s ease;
	&:hover {
		${DescriptionBody} {
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
			transition: 0.3s ease;
		}
	}
`

const FooterBody = styled(Body)`
	line-height: 1.5;
`

const FooterItem = styled(FlexDivRowCentered)`
	cursor: pointer;
	svg {
		path {
			transition: fill 0.3s ease;
		}
	}
	&:hover {
		${FooterBody} {
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
			transition: 0.3s ease;
		}
		svg {
			path {
				fill: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
			}
		}
	}
`

const MenuFooter = styled(GridDivCenteredRow)`
	grid-template-columns: 1fr 1fr;
	gap: 16px;
`
const Divider = styled(Spacer)`
	width: 100%;
`

const IconWrapper = styled.nav`
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 5px;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	position: relative;
	z-index: 50;
	&::before {
		content: '';
		position: absolute;
		top: -5px;
		left: -5px;
		right: -5px;
		bottom: -5px;
		z-index: -1;
	}

	&:hover {
		background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.button.default.hover.background};
		transition: 0.3s ease;
	}
`

const LabelIconWrapper = styled(IconWrapper)<{ $isActive: boolean }>`
	width: 42px;
	height: 42px;
	border-radius: 8px;
	background: ${(props) =>
		props.$isActive
			? props.theme.colors.selectedTheme.newTheme.button.solid.background
			: props.theme.colors.selectedTheme.newTheme.button.default.hover.background};
`

const AppsContainer = styled(FlexDivCol)`
	width: 350px;
	gap: 16px;
`

const DropdownContainer = styled.div<{
	open: boolean
	$isHome: boolean
	$addBannerOffset: boolean
}>`
	z-index: 100;
	position: absolute;
	top: ${(props) => (props.$isHome ? '52px' : '54px')};
	left: ${(props) => (props.$isHome ? '40px' : '15px')};
	width: 350px;
	opacity: ${(props) => (props.open ? 1 : 0)};
	visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
	transition: 0.3s ease;
	${media.lessThan('md')`
		width: calc(100% - 16px);
		left: 8px;
	`}
`

const ContaierSpacer = styled(DropdownContainer)`
	top: 35px;
	width: 85px;
	height: 25px;
	background-color: transparent;
`

const DropdownContent = styled(BaseCard)`
	display: flex;
	flex-direction: row;
	padding: 16px;
	border-radius: 8px;
	height: 100%;
	box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 5px 10px, rgba(0, 0, 0, 0.4) 0px 15px 40px;
`

export default MenuDropdown
